import React, { useEffect, useState } from "react";
import { IonButton, IonIcon, IonInput, IonItem, IonModal } from "@ionic/react";
import { closeOutline } from "ionicons/icons";

import ColorPicker from "../../ColorPicker";
import TeacherButton from "../../../atoms/button/TeacherButton";
import ClassSelectForm from "../../../molecules/ClassSelectForm";
import Toast from "../../../molecules/Toast";
import { IClass, TeacherCategory } from "../../../state";
import {
  CATEGORY_NAME_MAX_LENGTH,
  CATEGORY_NAME_TOO_LONG_MESSAGE,
} from "../../../store/common";

import styles from "./CategoryForm.module.scss";

type Props = {
  isOpen: boolean;
  assignedClasses: IClass[];
  category?: Omit<TeacherCategory, "klasses"> & { class_ids: number[] };
  calling: boolean;
  onClose: () => void;
  upsert: (params: {
    name: string;
    class_ids: number[];
    color: string;
  }) => void;
};
const CategoryForm = ({
  isOpen,
  assignedClasses,
  category,
  calling,
  onClose,
  upsert,
}: Props) => {
  const [isOpenClassSelectForm, setIsOpenClassSelectForm] = useState(false);
  const [form, setForm] = useState<
    Omit<TeacherCategory, "klasses"> & { class_ids: number[] }
  >({
    id: NaN,
    name: "",
    class_ids: [],
    color: "",
  });
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    category && setForm(category);
  }, [category]);

  const handleSendForm = () => {
    const toastMessageList = [];
    form.name === "" && toastMessageList.push("活かし先名");
    form.class_ids.length === 0 && toastMessageList.push("クラス");
    form.color === "" && toastMessageList.push("色");

    if (toastMessageList.length > 0) {
      const message = `${toastMessageList.join("、")}を入力してください`;
      setToastMessage(message);
      return;
    }

    if (Array.from(form.name).length > CATEGORY_NAME_MAX_LENGTH) {
      setToastMessage(CATEGORY_NAME_TOO_LONG_MESSAGE);
      return;
    }

    upsert(form);
    setForm({
      id: NaN,
      name: "",
      class_ids: [],
      color: "",
    });
    setIsOpenClassSelectForm(false);
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className={styles.wrapper}>
      <Toast
        type="danger"
        showToast={!!toastMessage}
        onClose={() => setToastMessage("")}
        message={toastMessage}
      />
      <div className={styles.window}>
        <div className={styles.header}>
          <p className={styles.title}>
            {category ? "活かし先を編集" : "活かし先をつくる"}
          </p>
          <IonButton className={styles.closeButton} onClick={onClose}>
            <IonIcon icon={closeOutline} className={styles.icon} />
          </IonButton>
        </div>
        <div className={styles.contents}>
          <IonItem className={styles.inputArea} lines="none">
            <IonInput
              value={form.name}
              className={styles.input}
              placeholder="活かし先名を記入"
              onIonChange={e =>
                setForm({ ...form, name: e.detail.value ?? "" })
              }
            />
          </IonItem>
          <ClassSelectForm
            isOpen={isOpenClassSelectForm}
            label="クラスを選択"
            classes={assignedClasses}
            multiple
            selected_class_ids={form.class_ids}
            className={styles.classSelectForm}
            whiteMode
            onClick={() => setIsOpenClassSelectForm(!isOpenClassSelectForm)}
            update={({ selected_class_ids: value }) =>
              setForm({ ...form, class_ids: value })
            }
          />
          <div className={styles.colorPickerContainer}>
            <p className={styles.subHeading}>活かし先の色を選択</p>
            <ColorPicker
              isHiddenLabel
              colorValue={form.color}
              update={({ color: value }) => setForm({ ...form, color: value })}
            />
          </div>
          <div className={styles.buttonContainer}>
            <TeacherButton
              customType="strong-outline"
              onClick={onClose}
              disabled={calling}
            >
              キャンセル
            </TeacherButton>
            <TeacherButton onClick={handleSendForm} disabled={calling}>
              保存
            </TeacherButton>
          </div>
        </div>
      </div>
    </IonModal>
  );
};

export default CategoryForm;
